import { supabase } from './supabaseClient';

export const CONTRACTOR_CHECKPOINTS = ['ARRIVED', 'SETUP_COMPLETE', 'PACK_DOWN', 'SHIFT_END'] as const;
export type ContractorCheckpoint = typeof CONTRACTOR_CHECKPOINTS[number];

export const CONTRACTOR_TEMPLATE_VARIABLES = [
  'recipient_name', 'status_label', 'event_name', 'event_date', 'event_time',
  'venue', 'crew_portal_url', 'event_reference'
] as const;

export type ContractorCheckpointRule = {
  enabled: boolean;
  delay_minutes: number;
  sms_body: string;
};

export type ContractorStatusConfig = {
  enabled: boolean;
  channel: 'SMS';
  escalation_enabled: false;
  rules: Record<ContractorCheckpoint, ContractorCheckpointRule>;
};

export type LoadedContractorStatusConfig = ContractorStatusConfig & {
  id: string;
  version: number;
  created_at: string;
};

export type ContractorReadiness = {
  upcoming_confirmed_assignments: number;
  upcoming_confirmed_portal_contractors: number;
  valid_mobile_count: number;
  missing_or_invalid_mobile_count: number;
  unresolved_roster_members: number;
  shifts_missing_timezone: number;
  shifts_missing_call_time: number;
  shifts_missing_setup_time: number;
  shifts_missing_pack_down_time: number;
  shifts_missing_end_time: number;
  contractor_delivery_enabled: false;
  external_delivery_enabled: false;
  kill_switch: true;
};

const defaultMessage = (label: string) =>
  `Reminder: please update your status to ${label} for {{event_name}} in the Crew App. {{crew_portal_url}}`;

export const DEFAULT_CONTRACTOR_STATUS_CONFIG: ContractorStatusConfig = {
  enabled: false,
  channel: 'SMS',
  escalation_enabled: false,
  rules: {
    ARRIVED: { enabled: false, delay_minutes: 0, sms_body: defaultMessage('Arrived on Site') },
    SETUP_COMPLETE: { enabled: false, delay_minutes: 0, sms_body: defaultMessage('Setup Complete') },
    PACK_DOWN: { enabled: false, delay_minutes: 0, sms_body: defaultMessage('Pack Down Complete') },
    SHIFT_END: { enabled: false, delay_minutes: 0, sms_body: defaultMessage('Shift Complete') }
  }
};

const variablePattern = /\{\{\s*([a-z_]+)\s*\}\}/g;

export function unknownContractorTemplateVariables(value: string): string[] {
  const names = Array.from(value.matchAll(variablePattern), match => match[1]);
  const unknown = names.filter(name => !(CONTRACTOR_TEMPLATE_VARIABLES as readonly string[]).includes(name));
  const withoutKnownTokens = value.replace(variablePattern, '');
  if (withoutKnownTokens.includes('{{') || withoutKnownTokens.includes('}}')) unknown.push('malformed variable');
  return Array.from(new Set(unknown));
}

export function validateContractorStatusConfig(config: ContractorStatusConfig): string[] {
  const errors: string[] = [];
  if (config.channel !== 'SMS') errors.push('Contractor Status Reminders support SMS only.');
  if (config.escalation_enabled) errors.push('Escalation remains unavailable until an authoritative Event Manager resolver exists.');
  const keys = Object.keys(config.rules).sort();
  if (keys.join(',') !== [...CONTRACTOR_CHECKPOINTS].sort().join(',')) errors.push('Configuration must contain exactly the supported checkpoints.');
  CONTRACTOR_CHECKPOINTS.forEach(checkpoint => {
    const rule = config.rules[checkpoint];
    if (!rule) return;
    if (!Number.isInteger(rule.delay_minutes) || rule.delay_minutes < 0 || rule.delay_minutes > 1440) {
      errors.push(`${checkpoint} delay must be between 0 and 1,440 minutes.`);
    }
    if (!rule.sms_body.trim() || rule.sms_body.length > 1600) errors.push(`${checkpoint} SMS body is required and must be 1,600 characters or fewer.`);
    const unknown = unknownContractorTemplateVariables(rule.sms_body);
    if (unknown.length) errors.push(`${checkpoint} has unknown template variable: ${unknown.join(', ')}.`);
  });
  return errors;
}

export function cloneContractorStatusConfig(config: ContractorStatusConfig): ContractorStatusConfig {
  return {
    enabled: config.enabled,
    channel: 'SMS',
    escalation_enabled: false,
    rules: Object.fromEntries(CONTRACTOR_CHECKPOINTS.map(checkpoint => [checkpoint, { ...config.rules[checkpoint] }])) as Record<ContractorCheckpoint, ContractorCheckpointRule>
  };
}

export async function loadContractorStatusConfig(): Promise<LoadedContractorStatusConfig | null> {
  const { data, error } = await supabase.rpc('admin_contractor_status_config');
  if (error) throw new Error(error.message);
  return data ? data as LoadedContractorStatusConfig : null;
}

export async function saveContractorStatusConfig(expectedVersion: number, config: ContractorStatusConfig): Promise<{ id: string; version: number; enabled: boolean }> {
  const errors = validateContractorStatusConfig(config);
  if (errors.length) throw new Error(errors.join(' '));
  const { data, error } = await supabase.rpc('admin_save_contractor_status_config', {
    p_expected_version: expectedVersion,
    p_config: config
  });
  if (error) throw new Error(error.message);
  return data as { id: string; version: number; enabled: boolean };
}

export async function loadContractorStatusReadiness(): Promise<ContractorReadiness> {
  const { data, error } = await supabase.rpc('admin_contractor_status_readiness');
  if (error) throw new Error(error.message);
  return data as ContractorReadiness;
}
